import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  DistrictDTO,
  DistrictSimpleDto,
} from 'src/app/common/dtos/district.dto';
import { User } from 'src/app/common/state/user/user.model';
import { UserService } from 'src/app/common/state/user/user.service';
import { UserState } from 'src/app/common/state/user/user.state';
import { SingleApiResponse } from 'src/app/common/types/responses/responses';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';
import { currentDistrictOrE2lEmployee } from 'src/app/common/utilities/check-route';
import { checkIfRemoteOperator } from 'src/app/common/utilities/role-helpers';
import { ProformPreviewModalComponent } from 'src/app/private/shared/components/modals/proform-preview-modal/proform-preview-modal.component';
import { ProformsSubmissionsModalComponent } from 'src/app/private/shared/components/proforms/proforms-submissions-modal/proforms-submissions-modal.component';
import { DistrictListService } from 'src/app/private/shared/services/district-list/district-list-service';
import { ProformsAdminService } from 'src/app/private/shared/services/proforms/proforms-admin.service';
import { ProformsFormListService } from 'src/app/private/shared/services/proforms/proforms-formlist.service';
import { NewProFormPayload } from 'src/app/private/shared/types/payloads/proforms.payloads';
import {
  GetAllProFormsAPIResponse,
  ProFormsAPIResponse,
  ProFormType,
} from 'src/app/private/shared/types/responses/proforms.responses';

@Component({
  selector: 'app-proforms-list-page',
  standalone: false,

  templateUrl: './proforms-list-page.component.html',
  styleUrl: './proforms-list-page.component.scss',
})
export class ProformsListPageComponent {
  @ViewChild('submissionModal')
  submissionModal: ProformsSubmissionsModalComponent;

  @ViewChild('previewModal')
  previewModal: ProformPreviewModalComponent;

  isLoading = true;

  isCreating = false;

  districtList: DistrictSimpleDto[] = [];

  district: DistrictDTO;

  districtCode: string;

  user: User | null;

  isRemoteOperator = false;

  pagination: PaginationDTO;

  currentPage = 1;

  submissionModalId: number;

  submissionModalTitle: string;

  forms: ProFormsAPIResponse[];

  isQuiz = false;

  ProFormType = ProFormType;

  searchTerm: string;

  searchTimeout: ReturnType<typeof setTimeout>;

  b2bDistricts: DistrictDTO[];

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private districtListService: DistrictListService,
    private store: Store,
    private proformsFormListService: ProformsFormListService,
    private proformsAdminService: ProformsAdminService,
    private userService: UserService
  ) {
    this.districtCode = this.route.snapshot.paramMap.get(
      'districtCode'
    ) as string;
    this.user = this.store.selectSnapshot(UserState.getUser);
    if (this.user) {
      this.isRemoteOperator = checkIfRemoteOperator(this.user);
      if (this.isRemoteOperator && this.user.district) {
        this.getB2b(this.user.district.id);
      }
    }
    this.districtListService
      .fetchDistrictsSimple({ per_page: 1000 })
      .subscribe((districts) => {
        if (districts) {
          this.districtList = districts;
          const district = districts.find(
            (searchDistrict) =>
              searchDistrict.districtCode === this.districtCode
          ) as DistrictDTO;
          if (district) {
            this.district = district;
          } else {
            this.district = districts.find(
              (searchDistrict) => searchDistrict.id === 2
            ) as DistrictDTO;
            this.districtCode = this.district.districtCode;
          }
          this.loadProForms();
        }
      });
  }

  loadProForms(): void {
    this.proformsFormListService
      .getForms(this.district.id, 10, this.currentPage)
      .subscribe((resp: GetAllProFormsAPIResponse) => {
        if (resp.items) {
          this.forms = resp.items;
          this.pagination = resp._meta;
          this.isLoading = false;
        }
      });
  }

  createForm() {
    this.isCreating = true;
    if (this.user) {
      const payload: NewProFormPayload = {
        title: 'New Form',
        outro: 'Your response has been submitted. Thank you for your input!',
        district_id: this.district?.id,
      };
      this.proformsFormListService
        .createForm(payload)
        .subscribe((resp: SingleApiResponse) => {
          if (resp.item.id) {
            setTimeout(() => {
              this.router.navigate([`/proforms/form-builder/${resp.item.id}`]);
            }, 1000);
          }
        });
    }
  }

  searchTermChanged(event: Event) {
    this.searchTerm = (<HTMLInputElement>event.target).value;
    this.searchDebounce();
  }

  searchDebounce() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.currentPage = 1;
      this.getProFormsWithSearchTerm();
    }, 300);
  }

  getProFormsWithSearchTerm() {
    this.proformsFormListService
      .getForms(
        this.district.id,
        10,
        this.currentPage,
        undefined,
        undefined,
        this.searchTerm
      )
      .subscribe((resp: GetAllProFormsAPIResponse) => {
        if (resp.items) {
          this.forms = resp.items;
          this.pagination = resp._meta;
          this.isLoading = false;
        }
      });
  }

  pageChange(newPage: number) {
    this.currentPage = newPage;
    this.loadProForms();
  }

  showSubmissionModal(formId: number, title: string, isQuiz: boolean) {
    this.submissionModalId = formId;
    this.submissionModalTitle = title;
    this.isQuiz = isQuiz;
    this.submissionModal.modal.open();
  }

  getB2b(id: number) {
    this.userService.getB2bDistricts(id).subscribe((response) => {
      this.b2bDistricts = response;
    });
  }

  userCanEdit(proform: ProFormsAPIResponse): boolean {
    return (
      currentDistrictOrE2lEmployee(this.user, { id: proform.district_id }) ||
      (this.isRemoteOperator &&
        this.b2bDistricts &&
        this.isInB2bDistrict(proform))
    );
  }

  isInB2bDistrict(proform: ProFormsAPIResponse) {
    if (proform.district_id) {
      const isInDistrict = this.b2bDistricts.find(
        (district) => district.id === proform.district_id
      );
      return !!isInDistrict;
    }
    return false;
  }

  previewForm(formId: number) {
    this.proformsAdminService.getProForm(formId);
    this.previewModal.openModal();
  }
}
